/**
 * Referrals (docs/REFERRALS.md). Pure: codes and invite ids, the share text, whether a referral counts, the months it
 * earns, where each invite stands, the trial / paid-month progress the referrer may see, and the five setup
 * milestones the invited owner may choose to share. The API, the web and the phone all use these words.
 *
 * The rules, as the owner set them (2026-09-28):
 * - R1 Every person has their own code and link: cleancleaver.com/r/<code>, and each "Text an invite" its own id.
 * - R2/R3 A business earns the referrer 2 free months, a home 1.
 * - R4 It counts only once the new business took a card and a subscription at sign-up, finished its 25-day free
 *   trial, and then paid for and completed one full paid month. Canceled, card removed or refunded before that:
 *   Revoked. A failed payment keeps it Pending until it is paid or canceled.
 * - R5 No self-referral: the same person, someone already in one of the referrer's businesses, or a business that
 *   shares a location address with one of the referrer's.
 * - R6 Months go to the business the referrer chooses; they stack.
 * - R7/R8 The invited business gets nothing extra, and the share text promises nothing but the free trial.
 * Privacy: the referrer sees only status facts (trial day, paid-month day, counts-on date, reasons) and, only with
 * the invited owner's consent, five yes/no setup milestones. Never logs, readings, reports, people or health.
 */
import { TRIAL_DAYS, trialDaysFor } from "./billing.ts";
import { JOIN_CODE_ALPHABET } from "./invites.ts";
import { isEndedStatus } from "./subscription.ts";
import type { Id, IsoDate, IsoDateTime, Location, OrgBilling, Organization } from "./model.ts";
import { expectedChecks, isSatisfied, type DayContext } from "./schedule.ts";

/* ─────────────── codes, ids and links ─────────────── */

export const REFERRAL_CODE_LENGTH = 7;
export const REFERRAL_INVITE_ID_LENGTH = 8;
/** Where people are sent: cleancleaver.com/r/<code>/<invite>. */
export const REFERRAL_ORIGIN = "https://cleancleaver.com";
/** R2, R3. */
export const REFERRAL_MONTHS = { business: 2, home: 1 } as const; // owner 2026-10-09: "instead of 3 months lets do 2"
export type ReferredKind = keyof typeof REFERRAL_MONTHS;
/** The same 25 free days as the plan (core billing.ts TRIAL_DAYS). */
export const REFERRAL_TRIAL_DAYS = TRIAL_DAYS;

/** cyrb53: a small, stable string hash (not for secrets: codes are not secret, they only say who invited). */
function hash53(s: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * A person's referral code: 7 symbols from the join-code alphabet (no 0/O/1/I/L), the same for the same user id.
 * `attempt` picks another code on the rare clash with someone else's (the database's unique index is the check).
 */
export function referralCode(userId: string, attempt = 0): string {
  const A = JOIN_CODE_ALPHABET;
  let n = hash53(`${userId}#${attempt}`), out = "";
  for (let i = 0; i < REFERRAL_CODE_LENGTH; i++) {
    if (n < A.length) n = hash53(`${userId}#${attempt}#${i}`);
    out += A[n % A.length];
    n = Math.floor(n / A.length);
  }
  return out;
}

/** An invite id from random bytes (8 symbols, rejection-sampled so each symbol is equally likely). */
export function referralInviteId(bytes: Uint8Array): string {
  const A = JOIN_CODE_ALPHABET, limit = 256 - (256 % A.length);
  let out = "";
  for (const b of bytes) {
    if (b >= limit) continue;
    out += A[b % A.length];
    if (out.length === REFERRAL_INVITE_ID_LENGTH) return out;
  }
  throw new Error("Not enough random bytes for an invite id.");
}

const codeOk = (v: string, len: number) => v.length === len && [...v].every((ch) => JOIN_CODE_ALPHABET.includes(ch));
/** What came in a link or was typed → the stored code, or null. Case, spaces and dashes don't matter. */
export function normalizeReferralCode(input: string): string | null {
  const v = String(input ?? "").toUpperCase().replace(/[\s-]/g, "");
  return codeOk(v, REFERRAL_CODE_LENGTH) ? v : null;
}
export function normalizeReferralInviteId(input: string): string | null {
  const v = String(input ?? "").toUpperCase().replace(/[\s-]/g, "");
  return codeOk(v, REFERRAL_INVITE_ID_LENGTH) ? v : null;
}

export function referralLink(origin: string, code: string, inviteId?: string): string {
  return `${origin.replace(/\/+$/, "")}/r/${code}${inviteId ? `/${inviteId}` : ""}`;
}

export interface ReferralShareText { smsBody: string; emailSubject: string; emailBody: string }
/**
 * The words the person sends from their own phone or email (we never send them). R8: only the free trial is
 * promised. https:// is dropped from the text (phones still make cleancleaver.com/... a link).
 */
export function referralShareText(link: string): ReferralShareText {
  const shown = link.replace(/^https:\/\//, "");
  const smsBody = `I use CleanCleaver to stay inspection-ready. Try it free for ${REFERRAL_TRIAL_DAYS} days: ${shown}`;
  return { smsBody, emailSubject: `Try CleanCleaver free for ${REFERRAL_TRIAL_DAYS} days`, emailBody: `Hi,\n\n${smsBody}\n` };
}

/* ─────────────── stored shapes ─────────────── */

export interface ReferralProfile {
  userId: Id;
  code: string;
  /** First name, shown to the invited owner ("Share your setup progress with Ana"). */
  name: string;
  email: string;
  /** R6: the business that gets the free months. Unset: their first business. */
  rewardOrgId?: Id;
  emailNotices: boolean;
  createdAt: IsoDateTime;
}
export type ReferralChannel = "text" | "email" | "link";
export interface ReferralInvite {
  id: string;
  userId: Id;
  code: string;
  label?: string;
  channel: ReferralChannel;
  createdAt: IsoDateTime;
  /** First visit of the link (counted once). */
  openedAt?: IsoDateTime;
}
export type ReferralStatus = "pending" | "counted" | "revoked" | "not_counted";
export interface Referral {
  id: Id;
  referrerUserId: Id;
  code: string;
  inviteId?: string;
  referredOrgId: Id;
  referredKind: ReferredKind;
  referredName: string;
  referredBy: Id;
  signedUpAt: IsoDateTime;
  status: ReferralStatus;
  reason?: string;
  /** Months earned: 0 until counted. */
  months: number;
  rewardOrgId?: Id;
  decidedAt?: IsoDateTime;
  /** The invited owner's consent to show their five setup milestones (default on, changeable any time). */
  shareProgress: boolean;
}
export type ReferralNoticeKind = "signed_up" | "counted";
/** One celebration for the referrer, shown once. A push could be sent from the same row later (pushedAt). */
export interface ReferralNotice {
  id: Id;
  userId: Id;
  referralId: Id;
  kind: ReferralNoticeKind;
  title: string;
  body: string;
  createdAt: IsoDateTime;
  seenAt?: IsoDateTime;
  emailedAt?: IsoDateTime;
  pushedAt?: IsoDateTime;
}

/* ─────────────── dates ─────────────── */

const DAY_MS = 86_400_000;
const TZ = "America/Denver";
/** The calendar day of an instant in a time zone. */
export function dayIn(at: Date | IsoDateTime, timeZone: string): IsoDate {
  const d = typeof at === "string" ? new Date(at) : at;
  const p = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}`;
}
/** Utah's day: the referral program's own dates (docs/REFERRALS.md). A business's billing and invite days use dayIn with its zone. */
export const utahDay = (at: Date | IsoDateTime): IsoDate => dayIn(at, TZ);
const dayNo = (d: IsoDate) => Math.round(Date.parse(`${d}T00:00:00Z`) / DAY_MS);
/** "Nov 22". */
export function prettyDay(d: IsoDate): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" }).format(new Date(`${d}T12:00:00Z`));
}
/** One calendar month later (Jan 31 → Feb 28/29), as providers bill. */
export function addMonth(iso: IsoDateTime): IsoDateTime {
  const d = new Date(iso);
  const day = d.getUTCDate();
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds()));
  const last = new Date(Date.UTC(out.getUTCFullYear(), out.getUTCMonth() + 1, 0)).getUTCDate();
  out.setUTCDate(Math.min(day, last));
  return out.toISOString();
}

/**
 * The free trial, from the card (owner 2026-09-29: "Trial starts when the card is added"). The end is Stripe's own
 * `trial_end` (billing.trialEndsAt, from the signed webhook); until that webhook arrives, the card's time + 25 days,
 * which is what Checkout's `trial_period_days: 25` gives. Day N counts the same whole days as the plan screen
 * (payments.ts trialDaysLeft): day = 25 - daysLeft + 1. Null when no card was ever added: no trial clock runs.
 */
export function referralTrial(org: Pick<Organization, "billing"> & { kind?: string | null }, now: Date): { day: number; of: number; lastDay: IsoDate; endsAt: IsoDateTime; over: boolean } | null {
  const b = org.billing;
  // A referred home's trial is a home's (HOME_TRIAL_DAYS, 3); a business's is the 25.
  const of = trialDaysFor(org.kind);
  const reported = b?.trialEndsAt && Number.isFinite(Date.parse(b.trialEndsAt)) ? b.trialEndsAt : null;
  const fromCard = b?.cardAddedAt && Number.isFinite(Date.parse(b.cardAddedAt)) ? new Date(Date.parse(b.cardAddedAt) + of * DAY_MS).toISOString() : null;
  const endsAt = reported ?? fromCard;
  if (!endsAt) return null;
  const ms = Date.parse(endsAt) - now.getTime();
  const left = Math.max(0, Math.ceil(ms / DAY_MS));
  const day = Math.min(of, Math.max(1, of - left + 1));
  return { day, of, lastDay: utahDay(endsAt), endsAt, over: ms <= 0 };
}

/* ─────────────── R5: no self-referral ─────────────── */

const ABBR: Record<string, string> = {
  street: "st", avenue: "ave", av: "ave", road: "rd", drive: "dr", boulevard: "blvd", lane: "ln", court: "ct", place: "pl", highway: "hwy",
  suite: "ste", apartment: "apt", unit: "unit", north: "n", south: "s", east: "e", west: "w", parkway: "pkwy", circle: "cir",
};
export interface AddressKey { street: string; city?: string; zip?: string }
/** "123 N. Main Street, Salt Lake City, UT 84101" → { street: "123 n main st", city: "salt lake city", zip: "84101" }. */
export function addressKey(address: string): AddressKey | null {
  const clean = address.toLowerCase().replace(/\(demo\)/g, " ").replace(/[.#]/g, " ");
  const parts = clean.split(",").map((p) => p.replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter(Boolean).map((w) => ABBR[w] ?? w).join(" ")).filter(Boolean);
  if (!parts[0] || !/\d/.test(parts[0])) return null; // no street number: too vague to compare
  const zip = clean.match(/\b(\d{5})(?:-\d{4})?\s*$/)?.[1];
  const city = parts[1] && !/^(ut|utah)( \d{5})?$/.test(parts[1]) ? parts[1] : undefined;
  return { street: parts[0], ...(city ? { city } : {}), ...(zip ? { zip } : {}) };
}
/** Same place: the same street line, and the same ZIP (or city) whenever both addresses have one. */
export function sameAddress(a: string, b: string): boolean {
  const x = addressKey(a), y = addressKey(b);
  if (!x || !y || x.street !== y.street) return false;
  if (x.zip && y.zip) return x.zip === y.zip;
  if (x.city && y.city) return x.city === y.city;
  return true;
}

export interface SelfReferralInput {
  referrer: { userId: Id; email?: string; businessOrgIds: Id[]; addresses: string[] };
  referred: {
    createdBy: Id;
    creatorEmail?: string;
    /** Every other organization the new business's creator belongs to. */
    creatorOrgIds: Id[];
    /** Owners and admins of the new business or home. */
    ownerUserIds: Id[];
    addresses: string[];
  };
}
export const SELF_REFERRAL_REASONS = {
  samePerson: "It's your own account, so it can't count.",
  sameTeam: "They're already on your team, so it can't count.",
  sameAddress: "It shares an address with one of your businesses, so it can't count.",
} as const;
/** R5: why this referral can't count, or null. */
export function selfReferralReason(i: SelfReferralInput): string | null {
  const { referrer: r, referred: n } = i;
  const same = (a?: string, b?: string) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  if (r.userId === n.createdBy || same(r.email, n.creatorEmail) || n.ownerUserIds.includes(r.userId)) return SELF_REFERRAL_REASONS.samePerson;
  if (n.creatorOrgIds.some((o) => r.businessOrgIds.includes(o))) return SELF_REFERRAL_REASONS.sameTeam;
  if (n.addresses.some((a) => r.addresses.some((b) => sameAddress(a, b)))) return SELF_REFERRAL_REASONS.sameAddress;
  return null;
}

/* ─────────────── R4: when it counts ─────────────── */

export type ReferralStage = "signed_up" | "trial" | "payment" | "paid_month" | "done";
export interface ReferralDecision {
  status: ReferralStatus;
  stage: ReferralStage;
  /** Plain words for the invite list. */
  reason: string;
  /** Utah day it counts, when known. */
  countsOn?: IsoDate;
}
export interface DecisionInput {
  kind: ReferredKind;
  org: Pick<Organization, "createdAt" | "billing">;
  /** From selfReferralReason. */
  selfReferral: string | null;
  now: Date;
}

export const REFERRAL_REASONS = {
  noCard: "Pending: counts when they add a card and their trial ends.",
  homeFree: "Pending: counts when the home adds a card and pays for its first full month.",
  firstPayment: "Pending: counts when their first payment goes through.",
  nextPayment: "Pending: counts when their payment goes through.",
  canceledTrial: "They canceled during the free trial.",
  cardRemoved: "They removed their card during the free trial.",
  canceledEarly: "They canceled before paying for a full month.",
  refunded: "Their first month was refunded.",
  unpaid: "Their payment didn't go through before they canceled.",
} as const;

/** The first paid month's end: the provider's period end, else one month after the first payment. */
export function firstPaidMonthEnd(b: OrgBilling): IsoDateTime | null {
  if (!b.subscribedAt) return null;
  return b.firstPaidPeriodEnd ?? addMonth(b.subscribedAt);
}

/**
 * Where a referral stands (R4, R5). Evaluated while it is Pending; Counted, Revoked and Not counted are final.
 * A home pays like a business (owner, 2026-10-14: 3 free days, then $18 a month), so the same rule applies to it; without
 * billing a home referral stays Pending with its plain reason.
 */
export function referralDecision(i: DecisionInput): ReferralDecision {
  if (i.selfReferral) return { status: "not_counted", stage: "done", reason: i.selfReferral };
  const b = i.org.billing;
  if (!b) return i.kind === "home" ? { status: "pending", stage: "signed_up", reason: REFERRAL_REASONS.homeFree } : { status: "pending", stage: "signed_up", reason: REFERRAL_REASONS.noCard };
  const end = firstPaidMonthEnd(b);
  if (!end) {
    // Not paid yet: the free trial, or a failed first payment.
    if (isEndedStatus(b.status)) return { status: "revoked", stage: "done", reason: REFERRAL_REASONS.canceledTrial };
    if (b.refundedAt) return { status: "revoked", stage: "done", reason: REFERRAL_REASONS.refunded };
    if (!b.cardOnFile) return b.cardAddedAt ? { status: "revoked", stage: "done", reason: REFERRAL_REASONS.cardRemoved } : { status: "pending", stage: "signed_up", reason: REFERRAL_REASONS.noCard };
    if (b.status === "past_due") return { status: "pending", stage: "payment", reason: REFERRAL_REASONS.firstPayment };
    const trial = referralTrial({ ...i.org, kind: i.kind }, i.now);
    if (!trial) return { status: "pending", stage: "trial", reason: "Pending: in their free trial." };
    const countsOn = utahDay(addMonth(trial.endsAt));
    return trial.over
      ? { status: "pending", stage: "payment", reason: REFERRAL_REASONS.firstPayment, countsOn }
      : { status: "pending", stage: "trial", reason: `Pending: in their free trial. Counts on ${prettyDay(countsOn)}, after their first paid month.`, countsOn };
  }
  const countsOn = utahDay(end);
  if (b.refundedAt) return { status: "revoked", stage: "done", reason: REFERRAL_REASONS.refunded };
  if (b.canceledAt && Date.parse(b.canceledAt) < Date.parse(end)) return { status: "revoked", stage: "done", reason: REFERRAL_REASONS.canceledEarly };
  if (i.now.getTime() < Date.parse(end)) return { status: "pending", stage: "paid_month", reason: `Pending: in their first paid month. Counts on ${prettyDay(countsOn)}.`, countsOn };
  // The first paid month is over. A failed renewal at its end keeps it Pending until paid (or canceled: not counted).
  if (b.status === "past_due") return { status: "pending", stage: "payment", reason: REFERRAL_REASONS.nextPayment, countsOn };
  if (isEndedStatus(b.status) && b.paymentFailedAt) return { status: "revoked", stage: "done", reason: REFERRAL_REASONS.unpaid };
  if (b.status === "active" || b.status === "canceled") return { status: "counted", stage: "done", reason: countedReason(i.kind), countsOn };
  return { status: "pending", stage: "payment", reason: REFERRAL_REASONS.nextPayment, countsOn };
}

/** "2 free months" / "1 free month". */
export const monthsWord = (n: number) => `${n} free month${n === 1 ? "" : "s"}`;
/** "2 months free": the menu label's form. */
export const monthsFree = (n: number) => `${n} month${n === 1 ? "" : "s"} free`;
/** The menu label and page title, everywhere (web rail, Me, phone More): the number comes from REFERRAL_MONTHS. */
export const REFERRAL_TITLE = `Invite a business, get ${monthsFree(REFERRAL_MONTHS.business)}`;
/** The invite page's lede. */
export const REFERRAL_LEDE = `Text a friend who runs a café, restaurant or kitchen. When their business pays for its first full month after the free trial, you get ${monthsWord(REFERRAL_MONTHS.business)}. A home that signs up gets you ${monthsWord(REFERRAL_MONTHS.home)}.`;
/** The phone's one-line hint. */
export const REFERRAL_HINT = `Text a friend's business your link. ${monthsWord(REFERRAL_MONTHS.business)} when they pay for a full month; ${REFERRAL_MONTHS.home} for a home.`;
export const countedReason = (kind: ReferredKind) => `Counted: ${monthsWord(REFERRAL_MONTHS[kind])}.`;

/** R4 + R5 in one yes/no. */
export function qualifies(i: DecisionInput): boolean {
  return referralDecision(i).status === "counted";
}

/** R2, R3, R6: months earned (counted, they stack) and months still pending. Revoked and Not counted add nothing. */
export function freeMonths(referrals: Pick<Referral, "status" | "referredKind" | "months" | "rewardOrgId">[]): { earned: number; pending: number; byOrg: { orgId: Id | null; months: number }[] } {
  let earned = 0, pending = 0;
  const by = new Map<Id | null, number>();
  for (const r of referrals) {
    if (r.status === "counted") { earned += r.months; by.set(r.rewardOrgId ?? null, (by.get(r.rewardOrgId ?? null) ?? 0) + r.months); }
    else if (r.status === "pending") pending += REFERRAL_MONTHS[r.referredKind];
  }
  return { earned, pending, byOrg: [...by].map(([orgId, months]) => ({ orgId, months })) };
}

/* ─────────────── what the referrer sees ─────────────── */

export type InviteState = "sent" | "opened" | "pending" | "counted" | "revoked" | "not_counted";
export const INVITE_STATE_LABEL: Record<InviteState, string> = { sent: "Sent", opened: "Opened", pending: "Pending", counted: "Counted", revoked: "Revoked", not_counted: "Not counted" };

export function inviteState(inv: Pick<ReferralInvite, "openedAt">, referral?: Pick<Referral, "status"> | null): InviteState {
  if (referral) return referral.status;
  return inv.openedAt ? "opened" : "sent";
}

export interface ReferralProgress {
  stage: ReferralStage;
  /** 0–100 toward counting. */
  pct: number;
  day?: number;
  of?: number;
  countsOn?: IsoDate;
  /** "Maria's Tacos · free trial day 12 of 25". Status facts only. */
  line: string;
}
/**
 * The progress bar and line on a row. Only status facts: the trial day, the paid-month day, the counts-on date and
 * reasons. Never anything about how the business uses the app.
 */
export function referralProgress(r: Pick<Referral, "status" | "reason" | "referredKind" | "referredName" | "months">, org: Pick<Organization, "createdAt" | "billing">, now: Date): ReferralProgress {
  const name = r.referredName;
  if (r.status === "counted") return { stage: "done", pct: 100, line: `${name} · counted: ${monthsWord(r.months)}` };
  if (r.status !== "pending") return { stage: "done", pct: 0, line: `${name} · ${r.reason ?? ""}`.trim() };
  const d = referralDecision({ kind: r.referredKind, org, selfReferral: null, now });
  const on = d.countsOn ? ` · counts on ${prettyDay(d.countsOn)}` : "";
  if (r.referredKind === "home" && !org.billing) return { stage: "signed_up", pct: 0, line: `${name} · no card yet` };
  if (d.stage === "paid_month") {
    const b = org.billing!, start = Date.parse(b.subscribedAt!), end = Date.parse(firstPaidMonthEnd(b)!);
    const of = Math.max(1, Math.round((end - start) / DAY_MS));
    const day = Math.min(of, Math.max(1, Math.floor((now.getTime() - start) / DAY_MS) + 1));
    return { stage: "paid_month", pct: Math.round(50 + (50 * day) / of), day, of, countsOn: d.countsOn, line: `${name} · paid month: day ${day} of ${of}${on}` };
  }
  if (d.stage === "payment") return { stage: "payment", pct: 50, countsOn: d.countsOn, line: `${name} · free trial over, waiting for their payment` };
  // No card: no trial clock (the 25 days start with the card), so no day count, only the plain fact.
  const t = d.stage === "signed_up" ? null : referralTrial({ ...org, kind: r.referredKind }, now);
  if (!t) return { stage: d.stage, pct: 0, countsOn: d.countsOn, line: d.stage === "signed_up" ? `${name} · no card yet` : `${name} · free trial${on}` };
  return { stage: d.stage, pct: Math.round((50 * t.day) / t.of), day: t.day, of: t.of, countsOn: d.countsOn, line: `${name} · free trial day ${t.day} of ${t.of}${on}` };
}

/* ─────────────── celebrations ─────────────── */

export function signedUpNotice(r: Pick<Referral, "referredName" | "referredKind">): { title: string; body: string } {
  const m = REFERRAL_MONTHS[r.referredKind];
  return {
    title: `${r.referredName} just signed up with your invite!`,
    body: `${monthsWord(m)} pending. They count once ${r.referredName} pays for a full month.`,
  };
}
export function countedNotice(r: Pick<Referral, "referredName" | "months">, rewardOrgName?: string): { title: string; body: string } {
  return {
    title: `${monthsWord(r.months)} added!`,
    body: `${r.referredName} paid for their first full month.${rewardOrgName ? ` The months go to ${rewardOrgName}.` : " Choose the business they go to on your invite page."}`,
  };
}
/** The notice email (sent through the normal email sender). Short, with the opt-out. */
export function noticeEmail(n: { title: string; body: string }): { subject: string; text: string } {
  return { subject: n.title, text: `${n.title} ${n.body}\n\nYou get this because you invited them to CleanCleaver. To stop these emails, turn off "Email me about my invites" on the Invite a business page.` };
}

/* ─────────────── setup milestones (shared only with consent) ─────────────── */

export type MilestoneId = "location" | "sanitizer" | "team" | "first_check" | "clean_week";
export const SETUP_MILESTONES: { id: MilestoneId; label: string; next: string }[] = [
  { id: "location", label: "Location set up", next: "set up your location" },
  { id: "sanitizer", label: "Sanitizer chosen", next: "pick your sanitizer" },
  { id: "team", label: "Team invited", next: "invite your team" },
  { id: "first_check", label: "First check logged", next: "log your first check" },
  { id: "clean_week", label: "First clean week", next: "log every check for a week" },
];
export interface MilestoneInput {
  locations: Pick<Location, "address" | "sanitizer">[];
  /** Invites sent plus other people on the team (a count only). */
  teamInvited: boolean;
  firstCheckLogged: boolean;
  cleanWeek: boolean;
}
export interface Milestone { id: MilestoneId; label: string; done: boolean }
/** Yes/no only. Uses what the app already records (locations, the sanitizer setting, invites, entries). */
export function setupMilestones(i: MilestoneInput): Milestone[] {
  const done: Record<MilestoneId, boolean> = {
    location: i.locations.some((l) => !!l.address?.trim()),
    sanitizer: i.locations.some((l) => !!l.sanitizer?.methods?.length),
    team: i.teamInvited,
    first_check: i.firstCheckLogged,
    clean_week: i.cleanWeek,
  };
  return SETUP_MILESTONES.map((m) => ({ id: m.id, label: m.label, done: done[m.id] }));
}
/** "Next: pick your sanitizer. Want me to show you?", or null when every milestone is done. */
export function nextStepText(ms: Milestone[]): string | null {
  const m = ms.find((x) => !x.done);
  if (!m) return null;
  const next = SETUP_MILESTONES.find((s) => s.id === m.id)!.next;
  return `Next: ${next}. Want me to show you?`;
}

/**
 * Every check that matters on one day was logged in its window (core schedule.ts expectedChecks + isSatisfied, the same
 * test the Today screen uses): the records the law names (`required`) and the opening checks "Open for food" waits on
 * (`blocking`; since fix/checks-by-law a recommendation is never `required`, so the gate counts here on its own). A "clean
 * week" is seven such days in a row (the reviews.ts `clean_week` idea, from the server's records).
 */
export function requiredChecksDone(ctx: DayContext): boolean {
  const req = expectedChecks(ctx).filter((e) => e.required || e.blocking);
  return req.length > 0 && req.every((e) => isSatisfied(e, ctx.entries));
}
export function hasCleanWeek(cleanDays: IsoDate[]): boolean {
  const n = [...new Set(cleanDays)].map(dayNo).sort((a, b) => a - b);
  let run = 0;
  for (let k = 0; k < n.length; k++) {
    run = k > 0 && n[k] === n[k - 1]! + 1 ? run + 1 : 1;
    if (run >= 7) return true;
  }
  return false;
}

export const firstNameOnly = (name?: string | null) => (name ?? "").trim().split(/\s+/)[0] ?? "";
